import { useRef } from 'react';

import { Button, Stack } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import type { PlanSelectNextStep } from '../utils';

interface Props {
  nextStep: PlanSelectNextStep | undefined;
  canSelfServe: boolean;
  isOwner: boolean;
  canClickPrev: boolean;
  canClickNext: boolean;
  onPrevClick: () => unknown;
  onNextClick: () => unknown;
  isCurrentProductSelected: boolean;
  productType: ProductType;
  isStandalone: boolean;
}

export const PlanSelectActions = ({
  nextStep,
  canSelfServe,
  isOwner,
  onPrevClick,
  onNextClick,
  isCurrentProductSelected,
  productType,
  isStandalone,
  // TODO: handle disabled button states
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canClickPrev,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canClickNext,
}: Props): JSX.Element | null => {
  const { t } = useTranslationWithPrefix('billing:plan-select-upsell-modal.cta');

  /**
   * If there's an error, we want to report it only once, even if the component re-renders multiple times with the
   * wrong data. The ref will store whether or not the error was thrown already.
   */
  const reportedError = useRef(false);

  const reportError = () => {
    if (!reportedError.current) {
      reportedError.current = true;
      throw new Error(
        'PlanSelectActions: could not display any button due to no match with any criteria.',
      );
    }
  };

  // If this is the last step and a non self serve product was selected
  if (!canSelfServe && nextStep === 'checkout') {
    return (
      <Stack direction="row" spacing="x2">
        <Button kind="ghost" onClick={onPrevClick} data-testid="plan-selection-modal-back-button">
          {t('back')}
        </Button>
        <Button onClick={onNextClick}>{t('sales')}</Button>
      </Stack>
    );
  }

  // If the current product is selected, show skip upgrade text
  if (isCurrentProductSelected && !isStandalone) {
    return (
      <Stack direction="row" spacing="x2">
        <Button kind="ghost" onClick={onPrevClick} data-testid="plan-selection-modal-back-button">
          {t('back')}
        </Button>
        <Button onClick={onNextClick}>
          {productType === ProductType.Analytics ? t('skip-observe') : t('skip-ask')}
        </Button>
      </Stack>
    );
  }

  if (nextStep === 'ask') {
    return (
      <Stack direction="row" spacing="x2">
        <Button kind="ghost" onClick={onPrevClick} data-testid="plan-selection-modal-back-button">
          {t('back')}
        </Button>
        <Button onClick={onNextClick} data-testid="plan-selection-modal-go-to-ask">
          {t('ask')}
        </Button>
      </Stack>
    );
  }

  if (nextStep === 'observe') {
    return (
      <Stack direction="row" spacing="x2">
        <Button kind="ghost" onClick={onPrevClick} data-testid="plan-selection-modal-back-button">
          {t('back')}
        </Button>
        <Button onClick={onNextClick}>{t('observe')}</Button>
      </Stack>
    );
  }

  if (nextStep === 'checkout' && isOwner) {
    return (
      <Stack direction="row" spacing="x2">
        <Button kind="ghost" onClick={onPrevClick} data-testid="plan-selection-modal-back-button">
          {t('back')}
        </Button>
        <Button onClick={onNextClick} data-testid="plan-selection-modal-checkout">
          {t('checkout')}
        </Button>
      </Stack>
    );
  }

  if (nextStep === 'checkout' && !isOwner) {
    return (
      <Stack direction="row" spacing="x2">
        <Button kind="ghost" onClick={onPrevClick} data-testid="plan-selection-modal-back-button">
          {t('back')}
        </Button>
        <Button onClick={onNextClick}>{t('non-owner')}</Button>
      </Stack>
    );
  }

  reportError();

  return null;
};
